import { Injectable } from "@angular/core";
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from "@angular/common/http";
import { Observable, switchMap, take, catchError, throwError } from "rxjs";
import { Store } from "@ngrx/store";
import { Router } from "@angular/router";
import { selectCurrentSite, selectCurrentSiteId } from "../auth/auth.selectors";
import { AuthActions } from "../auth/auth.actions";
import { SiteStorageService } from "../auth/services/site-storage.service";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private store: Store,
    private router: Router,
    private siteStorageService: SiteStorageService,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    /* withCredentials is necessary so that the server can set the cookie in the browser,
	  when web server address is different from api server address */
    /* X-App-ID header identifies this app for per-app site selection in database sessions */
    req = req.clone({
      withCredentials: true,
      setHeaders: {
        "X-App-ID": "bdp-con",
      },
    });

    if (this.shouldSkipInjection(req.url)) {
      return next.handle(req).pipe(
        catchError((error) => this.handleError(error, req)),
      );
    }

    return this.store.select(selectCurrentSite).pipe(
      take(1),
      switchMap((site) => {
        if (!site || !site.siteId || this.hasAuthSiteId(req)) {
          return next.handle(req).pipe(
            catchError((error) => this.handleError(error, req)),
          );
        }

        let modifiedReq = req;
        if (req.method === "GET" || req.body instanceof FormData) {
          // GET requests have no body to inject into; multipart uploads can't
          // carry authSiteId in a JSON body either. Both fall back to the
          // ?asid= query parameter, which IAmAuthorizedMW reads first.
          let params = req.params.set("asid", site.siteId);
          modifiedReq = req.clone({ params });
        } else {
          let body = req.body
            ? { ...req.body, authSiteId: site.siteId }
            : { authSiteId: site.siteId };
          modifiedReq = req.clone({ body });
        }

        return next.handle(modifiedReq).pipe(
          catchError((error) => this.handleError(error, req)),
        );
      }),
    );
  }

  /**
   * Handle HTTP errors, particularly 401 Unauthorized responses.
   * This enables logout sync across apps sharing the same BDPSSNID cookie.
   * When one app logs out (clearing the cookie), other apps will get 401
   * on their next API call and be redirected to login.
   *
   * On HTTP 403 (Forbidden — authenticated but lacks the required permission)
   * we re-fetch the permission list for the current site so the menu can
   * drop items the user no longer has access to. Without this, the menu
   * keeps showing a stale entry (e.g. Messages with no message.send) and
   * the user only finds out by clicking it and getting denied.
   */
  private handleError(error: HttpErrorResponse, req: HttpRequest<any>): Observable<never> {
    if (error.status === 401 && !this.shouldSkipLogoutOnError(req.url)) {
      // Session expired or logged out from another app
      // Clear local state and redirect to login
      this.siteStorageService.clearSiteData();
      this.store.dispatch(AuthActions.logoutSuccess());
      this.router.navigate(["/login"]);
    } else if (error.status === 403 && !this.shouldSkipPermRefreshOnError(req.url)) {
      this.store.dispatch(AuthActions.refreshPermissions());
    }
    return throwError(() => error);
  }

  /**
   * URLs that should not trigger logout on 401.
   * Login/logout endpoints return 401 for invalid credentials, not session expiry.
   */
  private shouldSkipLogoutOnError(url: string): boolean {
    const skipUrls = ["/auth/login", "/auth/logout", "/auth/me"];
    return skipUrls.some((skipUrl) => url.includes(skipUrl));
  }

  /**
   * URLs that should not trigger a permissions refresh on 403. The refresh
   * itself goes to /permissioncode/list/ — looping that back on its own
   * failure would spin forever. /auth/* failures aren't permission denials.
   */
  private shouldSkipPermRefreshOnError(url: string): boolean {
    const skipUrls = ["/permissioncode/list", "/auth/login", "/auth/logout"];
    return skipUrls.some((skipUrl) => url.includes(skipUrl));
  }

  private shouldSkipInjection(url: string): boolean {
    const skipUrls = ["/auth/login", "/auth/logout", "/auth/refresh", "/permissioncode/"];

    return skipUrls.some((skipUrl) => url.includes(skipUrl));
  }

  private hasAuthSiteId(req: HttpRequest<any>): boolean {
    const hasInParams =
      req.params.has("authSiteId") ||
      req.params.has("asid") ||
      req.params.has("authSubsiteId") ||
      req.params.has("assid");
    const hasInBody =
      req.body && (req.body.authSiteId || req.body.authSubsiteId);
    return hasInParams || hasInBody;
  }
}
